import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const BrandContext = createContext({
  brand_name: null,
  brand_logo_b64: null,
  brand_accent_color: null,
  trainer_name: null,
  refresh: () => {},
});

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const [brand, setBrand] = useState({
    brand_name: null,
    brand_logo_b64: null,
    brand_accent_color: null,
    trainer_name: null,
  });

  const refresh = async () => {
    if (!user?.id) {
      setBrand({ brand_name: null, brand_logo_b64: null, brand_accent_color: null, trainer_name: null });
      return;
    }
    try {
      const { data } = await api.get("/branding/me");
      setBrand({
        brand_name: data?.brand_name || null,
        brand_logo_b64: data?.brand_logo_b64 || null,
        brand_accent_color: data?.brand_accent_color || null,
        trainer_name: data?.trainer_name || null,
        whatsapp_number: data?.whatsapp_contact_number || null,
      });
    } catch (_e) {
      /* not logged in or no trainer — silently ignore */
    }
  };

  useEffect(() => { refresh(); }, [user?.id]);

  // Apply accent color as CSS variables. The whole UI uses Tailwind `brand`,
  // `brand-light`, `brand-lighter` colors backed by --brand-*-rgb vars, so the
  // coach's chosen accent themes every page (dashboard, nav, buttons, charts).
  useEffect(() => {
    const root = document.documentElement;
    const hexToRgb = (hex) => {
      const h = hex.replace("#", "");
      const f = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
      return [parseInt(f.slice(0, 2), 16), parseInt(f.slice(2, 4), 16), parseInt(f.slice(4, 6), 16)];
    };
    const mixWhite = ([r, g, b], t) => [
      Math.round(r + (255 - r) * t),
      Math.round(g + (255 - g) * t),
      Math.round(b + (255 - b) * t),
    ];
    const toHex = ([r, g, b]) => `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;

    const hex = brand.brand_accent_color;
    if (hex && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) {
      const base = hexToRgb(hex);
      const light = mixWhite(base, 0.3);
      const lighter = mixWhite(base, 0.55);
      root.style.setProperty("--brand-accent", hex);
      root.style.setProperty("--brand", hex);
      root.style.setProperty("--brand-light", toHex(light));
      root.style.setProperty("--brand-lighter", toHex(lighter));
      root.style.setProperty("--brand-rgb", base.join(" "));
      root.style.setProperty("--brand-light-rgb", light.join(" "));
      root.style.setProperty("--brand-lighter-rgb", lighter.join(" "));
    } else {
      ["--brand-accent", "--brand", "--brand-light", "--brand-lighter", "--brand-rgb", "--brand-light-rgb", "--brand-lighter-rgb"].forEach((v) => root.style.removeProperty(v));
    }
  }, [brand.brand_accent_color]);

  return (
    <BrandContext.Provider value={{ ...brand, refresh }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}
